/** "Tải tờ in PDF": pick the paper and the number of copies; the PDF is made here from the result JPEG (sheet.ts).
 * An AI job: also pick which photo goes on the sheet, the AI one or the one processed without AI. A job of several
 * specs (GĐ4): how many of each spec, each on rows of its own ("tờ in trộn quy cách"). */
import { useId, useMemo, useRef, useState } from "react";
import type { Api, Job } from "../api";
import { AI_RESULT, errorMessage } from "../messages";
import { DEFAULT_PAPERS, capacity, cutMarks, layout, mixedLayout, sheetPdf, sheetPdfMulti, type Paper } from "../sheet";
import { Modal } from "./Modal";
import { SheetPreview } from "./SheetPreview";

/** GĐ4: one spec of the job on the sheet: its name, size and files ("photo" / "photo_2"…, "local" / "local_2"…). */
export interface SheetPart {
  label: string;
  mm: { width: number; height: number };
  photo: string;
  local?: string;
}

interface Props {
  api: Api;
  job: Job;
  photoMm: { width: number; height: number };
  papers?: Paper[];
  /** several specs: the sheet takes some of each */
  parts?: SheetPart[];
  onClose: () => void;
}

const PREFERRED = ["photo_10x15", "a4"]; // the render machine's own sheet: 10 x 15 cm, else A4

type Which = "photo" | "local";

/** The bytes of a result file; signed links last 15 minutes, so a refused one is asked for again. */
export async function fetchJobFile(api: Api, job: Job, which: string): Promise<Blob> {
  const get = (path: string) => fetch(api.fileUrl(path), { credentials: "include" });
  const files = job.files as Record<string, string | undefined>;
  const first = files[which];
  let resp = first ? await get(first) : null;
  if (!resp || !resp.ok) {
    const fresh = ((await api.job(job.id)).files as Record<string, string | undefined>)[which];
    if (!fresh) throw new Error("file_not_found");
    resp = await get(fresh);
  }
  if (!resp.ok) throw new Error("file_not_found");
  return resp.blob();
}

async function fetchPhoto(api: Api, job: Job, which: string): Promise<Uint8Array> {
  return new Uint8Array(await (await fetchJobFile(api, job, which)).arrayBuffer());
}

function save(pdf: Uint8Array, name: string) {
  const href = URL.createObjectURL(new Blob([pdf as BlobPart], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 30_000);
}

export function PrintDialog(props: Props) {
  return props.parts && props.parts.length > 1 ? <MixedPrintDialog {...props} parts={props.parts} /> : <OneSpecDialog {...props} />;
}

function OneSpecDialog({ api, job, photoMm, papers, onClose }: Props) {
  const titleId = useId();
  const paperId = useId();
  const copiesId = useId();
  const whichId = useId();
  const [which, setWhich] = useState<Which>("photo");
  const list = (papers && papers.length > 0 ? papers : DEFAULT_PAPERS)
    .filter((p) => capacity(p, photoMm.width, photoMm.height) > 0);
  const first = PREFERRED.map((k) => list.find((p) => p.key === k)).find(Boolean) ?? list[0];
  const [paperKey, setPaperKey] = useState(first?.key ?? "");
  const paper = list.find((p) => p.key === paperKey) ?? first;
  const max = paper ? capacity(paper, photoMm.width, photoMm.height) : 0;
  const [copies, setCopies] = useState(max);
  const n = Math.min(Math.max(1, copies || 1), Math.max(1, max));
  const cells = useMemo(() => (paper ? layout(paper, photoMm.width, photoMm.height, n) : []), [paper, photoMm, n]);
  const marks = useMemo(() => (paper ? cutMarks(cells, paper.width_mm, paper.height_mm) : []), [paper, cells]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const downloadRef = useRef<HTMLButtonElement>(null);

  async function download() {
    if (!paper) return;
    setBusy(true);
    setError(null);
    try {
      save(sheetPdf(await fetchPhoto(api, job, which), paper, cells), `anh-the-${job.id.slice(0, 8)}-${paper.key}-${n}-anh.pdf`);
      onClose();
    } catch (e) {
      setError(errorMessage(e instanceof Error && e.message === "file_not_found" ? "file_not_found" : "network"));
      setBusy(false);
    }
  }

  return (
    <Modal titleId={titleId} onClose={onClose} initialFocus={downloadRef}>
      <h2 id={titleId}>Tải tờ in PDF</h2>
      {!paper ? (
        <p className="error">Ảnh này lớn hơn mọi khổ giấy có sẵn.</p>
      ) : (
        <div className={`print-form${job.files.local ? " has-which" : ""}`}>
          {job.files.local && (
            <fieldset className="field print-which">
              <legend id={whichId}>{AI_RESULT.sheetFrom}</legend>
              <div className="segmented" role="radiogroup" aria-labelledby={whichId}>
                {(["photo", "local"] as Which[]).map((w) => (
                  <label key={w} className={`segment${which === w ? " is-selected" : ""}`}>
                    <input type="radio" name={whichId} value={w} checked={which === w} onChange={() => setWhich(w)} />
                    {w === "photo" ? AI_RESULT.ai : AI_RESULT.local}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <div className="field">
            <label htmlFor={paperId}>Cỡ giấy</label>
            <select id={paperId} value={paper.key} onChange={(e) => {
              const next = list.find((p) => p.key === e.target.value);
              setPaperKey(e.target.value);
              if (next) setCopies(capacity(next, photoMm.width, photoMm.height));
            }}>
              {list.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor={copiesId}>Số ảnh trên tờ</label>
            <input id={copiesId} type="number" inputMode="numeric" min={1} max={max} value={copies}
                   onChange={(e) => setCopies(Number(e.target.value))} />
            <small className="hint">Khổ này chứa tối đa {max} ảnh.</small>
          </div>
          {/* the photo itself in the cells (same aspect as the spec), as the editor's "Xếp tờ in" shows it */}
          <SheetPreview paper={[paper.width_mm, paper.height_mm]} cells={cells} marks={marks}
                        label={`Xem trước: ${n} ảnh trên ${paper.label}`}
                        photo={job.files[which] ? { url: api.fileUrl(job.files[which] as string), width: photoMm.width,
                                                     height: photoMm.height } : null} />
          <p className="hint">In ở cỡ thật (100 %, không chọn “Vừa trang / Fit to page”), rồi cắt theo vạch.</p>
        </div>
      )}
      {error && <p role="alert" className="error">{error}</p>}
      <div className="actions actions-end">
        <button type="button" className="btn btn-quiet" onClick={onClose}>Đóng</button>
        <button ref={downloadRef} type="button" className="btn btn-primary" disabled={!paper || busy}
                onClick={() => void download()}>
          {busy ? "Đang tạo PDF…" : `Tải PDF (${n} ảnh)`}
        </button>
      </div>
    </Modal>
  );
}

/** GĐ4: several specs on one sheet — how many of each, each spec on rows of its own, one PDF. */
function MixedPrintDialog({ api, job, papers, parts, onClose }: Props & { parts: SheetPart[] }) {
  const titleId = useId();
  const paperId = useId();
  const whichId = useId();
  const baseId = useId();
  const hasLocal = parts.every((p) => p.local && (job.files as Record<string, string | undefined>)[p.local]);
  const [which, setWhich] = useState<Which>("photo");
  const list = (papers && papers.length > 0 ? papers : DEFAULT_PAPERS)
    .filter((p) => parts.every((part) => capacity(p, part.mm.width, part.mm.height) > 0));
  const fits = (p: Paper, n: number) => mixedLayout(p, parts.map((part) => ({ w: part.mm.width, h: part.mm.height, copies: n }))) !== null;
  // the first paper (10 x 15 cm, else A4, else any) that holds one of each; as many of each as it holds (up to 4)
  const first = [...PREFERRED.map((k) => list.find((p) => p.key === k)), ...list].find((p) => p && fits(p, 1)) ?? list[0];
  const [paperKey, setPaperKey] = useState(first?.key ?? "");
  const paper = list.find((p) => p.key === paperKey) ?? first;
  const [copies, setCopies] = useState<number[]>(() => {
    const n = first ? [4, 3, 2, 1].find((k) => fits(first, k)) ?? 1 : 1;
    return parts.map(() => n);
  });
  const cells = useMemo(() => (paper ? mixedLayout(paper, parts.map((p, i) => ({ w: p.mm.width, h: p.mm.height,
                                                                                   copies: copies[i] }))) : null),
                        [paper, parts, copies]);
  const marks = useMemo(() => (paper && cells ? cutMarks(cells, paper.width_mm, paper.height_mm) : []), [paper, cells]);
  const total = copies.reduce((a, b) => a + b, 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const downloadRef = useRef<HTMLButtonElement>(null);
  const fileOf = (p: SheetPart) => (which === "local" && p.local ? p.local : p.photo);
  const urls = parts.map((p) => (job.files as Record<string, string | undefined>)[fileOf(p)]);

  async function download() {
    if (!paper || !cells || !cells.length) return;
    setBusy(true);
    setError(null);
    try {
      const jpegs = await Promise.all(parts.map((p) => fetchPhoto(api, job, fileOf(p))));
      save(sheetPdfMulti(jpegs, paper, cells), `anh-the-${job.id.slice(0, 8)}-${paper.key}-${total}-anh.pdf`);
      onClose();
    } catch (e) {
      setError(errorMessage(e instanceof Error && e.message === "file_not_found" ? "file_not_found" : "network"));
      setBusy(false);
    }
  }

  return (
    <Modal titleId={titleId} onClose={onClose} initialFocus={downloadRef}>
      <h2 id={titleId}>Tải tờ in PDF</h2>
      {!paper ? (
        <p className="error">Không có khổ giấy nào chứa được mọi quy cách.</p>
      ) : (
        <div className={`print-form${hasLocal ? " has-which" : ""}`}>
          {hasLocal && (
            <fieldset className="field print-which">
              <legend id={whichId}>{AI_RESULT.sheetFrom}</legend>
              <div className="segmented" role="radiogroup" aria-labelledby={whichId}>
                {(["photo", "local"] as Which[]).map((w) => (
                  <label key={w} className={`segment${which === w ? " is-selected" : ""}`}>
                    <input type="radio" name={whichId} value={w} checked={which === w} onChange={() => setWhich(w)} />
                    {w === "photo" ? AI_RESULT.ai : AI_RESULT.local}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <div className="field">
            <label htmlFor={paperId}>Cỡ giấy</label>
            <select id={paperId} value={paper.key} onChange={(e) => setPaperKey(e.target.value)}>
              {list.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          {parts.map((p, i) => (
            <div key={p.photo} className="field">
              <label htmlFor={`${baseId}-${i}`}>Số ảnh {p.label}</label>
              <input id={`${baseId}-${i}`} type="number" inputMode="numeric" min={0} max={50} value={copies[i]}
                     onChange={(e) => {
                       const v = Math.max(0, Math.min(50, Math.trunc(Number(e.target.value) || 0)));
                       setCopies((c) => c.map((x, j) => (j === i ? v : x)));
                     }} />
            </div>
          ))}
          {cells ? (
            <SheetPreview paper={[paper.width_mm, paper.height_mm]} cells={cells} marks={marks}
                          label={`Xem trước: ${total} ảnh trên ${paper.label}`}
                          photos={parts.map((p, i) => ({ url: urls[i] ? api.fileUrl(urls[i] as string) : "", width: p.mm.width,
                                                         height: p.mm.height }))} />
          ) : <p role="alert" className="error">Không vừa một tờ {paper.label}: bớt số ảnh hoặc chọn khổ giấy lớn hơn.</p>}
          <p className="hint">Mỗi quy cách một hàng riêng. In ở cỡ thật (100 %, không chọn “Vừa trang / Fit to page”), rồi cắt theo vạch.</p>
        </div>
      )}
      {error && <p role="alert" className="error">{error}</p>}
      <div className="actions actions-end">
        <button type="button" className="btn btn-quiet" onClick={onClose}>Đóng</button>
        <button ref={downloadRef} type="button" className="btn btn-primary" disabled={!paper || !cells || !total || busy}
                onClick={() => void download()}>
          {busy ? "Đang tạo PDF…" : `Tải PDF (${total} ảnh)`}
        </button>
      </div>
    </Modal>
  );
}
